import { Component, HostListener, signal, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, OnInit, OnDestroy, Output, EventEmitter, effect, computed, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ThemeService } from '../../core/services/theme/theme.service';
import { NotificationDropdownComponent } from '../../shared/components/notification-dropdown/notification-dropdown';
import { AuthService } from '../../core/services/Auth/auth-service';
import { NavigationService, NavigationItem, NavigationSubItem } from '../../core/services/sidebar/sidebar.service';
import { Subscription, take } from 'rxjs';
import { LayoutService } from '../../core/services/layout/layout.service';
import { ApiService } from '@app/core/services/API/api-service';
import { ClientSummarySlider } from '../../layout/client-summary-slider';
import { MyWidgetsSlider } from '../../layout/my-widgets-slider/my-widgets-slider';
import { FeedbackForm } from '../../shared/components/feedback-form/feedback-form';
import { MatIconModule } from '@angular/material/icon';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';
import { RecentItemsService } from '@app/shared/services/recent-items.service';
import { ThemeManagementService } from '@app/core/services/theme/theme-management.service';
import { UserSettings } from '@app/shared/components/my-settings/my-settings';
import { UserSettingsService } from '@app/shared/services/user-settings.service';
import { NotificationService } from '@app/core/services/notifications/notification.service';
import { SearchCategoryService } from '@app/core/services/search/search-category.service';
import { FestivalDecoration } from '@app/shared/components/festival-decoration/festival-decoration';
import { ToastrService } from 'ngx-toastr';
import {
  ClientSummarySliderFilter,
  ClientSummarySliderOpenRequest,
  ClientSummarySyncService,
} from '@app/core/services/client-summary-sync/client-summary-sync.service';
import { AddRequestForm } from '@app/pages/projects/requests/add-request-form/add-request-form';
import { RequestService } from '@app/pages/projects/projects.service';
import { CrossTabSyncService } from '@app/core/services/cross-tab-sync/cross-tab-sync.service';
import { TimesheetStatusService } from '@app/core/services/timesheet-status/timesheet-status.service';
import { AiHelpAssistantFab } from '@app/features/ai-help-assistant';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NotificationDropdownComponent,
    ClientSummarySlider,
    MyWidgetsSlider,
    FeedbackForm,
    MatIconModule,
    ...matTooltipSmartImports,
    FestivalDecoration,
    AddRequestForm,
    AiHelpAssistantFab,
  ],
  templateUrl: './header.html',
  styleUrls: ['./header.scss']
})
export class Header implements OnInit, OnDestroy {

  @ViewChild('itemsContainer') itemsContainer!: ElementRef;
  @ViewChild('headerAddRequestForm') headerAddRequestForm?: AddRequestForm;
  @ViewChild('headerSearchInput') headerSearchInput?: ElementRef<HTMLInputElement>;
  
  // Add class to host element when client summary or my widgets slider is open (raises z-index above right sidebar).
  // Keep elevated during my-widgets leave animation so it doesn't go under the notification section.
  @HostBinding('class.client-summary-slider-open') get isSliderOpen() {
    return (
      this.showClientSummarySlider ||
      this.showMyWidgetsSlider ||
      this.showFeedbackSlider ||
      this.showAddRequestForm ||
      this.myWidgetsSliderClosing
    );
  }
  /** Raises header above fixed right sidebar while the notification panel is open */
  @HostBinding('class.notifications-open') get isNotificationsPanelOpen() {
    return this.notificationsOpen();
  }
  /** Keeps the more menu above the fixed sticky breadcrumb (z-index 999). */
  @HostBinding('class.header-more-open') get isHeaderMoreOpen() {
    return this.headerMoreOpen();
  }
  /** True during my-widgets leave animation so header stays above right sidebar until animation completes */
  private myWidgetsSliderClosing = false;

  navActive = false;
  showWidgetsBtn = false;
  isMainPage = false; // For header navigation highlighting
  dropdownOpen = signal(false);
  notificationsOpen = signal(false);
  searchDropdownOpen = signal(false);
  moreDropdownOpen = signal(false);
  readonly headerMoreOpen = signal(false);
  private headerMoreCloseTimer = 0;
  private headerMoreLockedClosed = false;
  isDropdownOpen = false;
  themeIconAnimating = false;
  private themeIconAnimTimer: ReturnType<typeof setTimeout> | null = null;

  searchQuery = signal('');
  selectedSearchOption = signal('All');
  showRecentArrows = false;
  canScrollLeft = false;
  canScrollRight = false;
  recentlyViewedExpanded = signal(false);
  showClientSummarySlider = false;
  showClientSummaryButton = false;
  sliderOpenRequest: ClientSummarySliderOpenRequest | null = null;
  showMyWidgetsSlider = false;
  showFeedbackSlider = false;
  showAddRequestForm = false;
  searchDisabled = signal(false); // Enable search functionality


  // recentItems = signal([
  //   'Project A - Task 1 | sgkghfsk shjdfyuewur ueyruieyru',
  //   'Portfolio X - Update | sgkghfsk shjdfyuewur ueyruieyru',
  //   'Request 123 - Client Y | sgkghfsk shjdfyuewur ueyruieyru',
  //   'Issue #456 - Bug Fix | sgkghfsk shjdfyuewur ueyruieyru',
  //   'Project B - Meeting Notes | sgkghfsk shjdfyuewur ueyruieyru'
  // ]);
  recentItems = signal<{ _id: string, projectName: string }[]>([]);
  headerItems: NavigationItem[] = [];
  activeHeader: NavigationItem | null = null;
  private subscription = new Subscription();
  allowedRoutes = ['/tasks'];

  isSidebarCollapsed = false;
  userName: string | null = null;
  userId: string | null = null;
  userImage: string | null = null;
  profileimage: string | null = null;
  private trackedSessionUserId: string | null = null;

  @Output() changePasswordClick = new EventEmitter<void>();
  @Output() backgroundImageSelected = new EventEmitter<string>();
  @Output() backgroundImagePreview = new EventEmitter<string>();
  @Output() themeBtnClick = new EventEmitter<void>();
  @Output() settingsClick = new EventEmitter<void>();
  @Output() profileDropdownStateChange = new EventEmitter<{ isOpen: boolean, userName: string | null, userImage: string | null }>();
  @Output() accountSwitchedBack = new EventEmitter<void>();
  @Output() switchBackLoadingChange = new EventEmitter<boolean>();
  @Output() logoutLoadingChange = new EventEmitter<boolean>();

  switchBackLoading = signal(false);
  logoutLoading = signal(false);

  // Add properties to track cleanup
  private scrollListener: (() => void) | null = null;
  private clickListeners: Array<{ element: Document | Element, event: string, handler: EventListener }> = [];
  // Add private property for storage listener
  private _storageListener?: (event: StorageEvent) => void;
  unreadNotifications = computed(() => this.notificationService.unseenCount());
  readonly isManagerUser = computed(
    () => this.authService.userData()?.accesslevelType === 'Manager'
  );

  constructor(
    public themeService: ThemeService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private navigationService: NavigationService,
    public layoutService: LayoutService,
     private recentItemsService: RecentItemsService,
     private themeManagementService: ThemeManagementService,
     private userSettingsService: UserSettingsService,
     public notificationService: NotificationService,
     private searchCategoryService: SearchCategoryService,
     private toastr: ToastrService,
     private clientSummarySync: ClientSummarySyncService,
     private requestService: RequestService,
     private crossTabSyncService: CrossTabSyncService,
     public timesheetStatusService: TimesheetStatusService
  ) {
    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
        this.showWidgetsBtn = event.urlAfterRedirects.includes('/dashboard') || event.urlAfterRedirects.includes('/single-dashboard');
        this.isMainPage = event.urlAfterRedirects === '/' || 
                         event.urlAfterRedirects === '/dashboard' || 
                         event.urlAfterRedirects === '/projects' || 
                         event.urlAfterRedirects.startsWith('/projects/');
        
        // Handle client summary button visibility
        this.updateClientSummaryButtonVisibility();
        
        // Sync search query from URL params when navigating
        if (event.urlAfterRedirects.includes('/search')) {
          // Get query params from the current route snapshot
          // Use setTimeout to ensure route snapshot is updated
          setTimeout(() => {
            const snapshot = this.route.snapshot;
            const queryParams = snapshot.queryParams;
            if (queryParams['q']) {
              this.searchQuery.set(queryParams['q']);
            } else {
              this.searchQuery.set('');
            }
            this.cdr.detectChanges();
          }, 0);
        } else {
          // Clear search query when navigating away from search page
          this.searchQuery.set('');
        }
        
        // Update scroll state based on route change
        if (event.urlAfterRedirects.includes('/clients') || event.urlAfterRedirects.includes('/clients-list')) {
          this.isScrolled = false;
          this.isClientPageScrolled = false; // Reset client scroll state
          this.cdr.detectChanges();
        } else {
          // For other routes, check if we need to reset scroll state
          this.updateScrollStateForRoute();
        }
      }
      })
    );
     // Listen for recent items refresh events using Angular signals effect
    effect(() => {
       this.recentItemsService.recentItemsRefreshSignal();
      this.loadRecentItems();
      this.cdr.detectChanges();
    });

    // Listen for search category changes from search page
    effect(() => {
      const category = this.searchCategoryService.selectedCategory();
      if (category !== this.selectedSearchOption()) {
        this.selectedSearchOption.set(category);
        this.cdr.detectChanges();
      }
    });

    effect(() => {
      const userData = this.authService.userData();
      const userId = userData?._id ?? null;
      if (!userId || userId === this.trackedSessionUserId) {
        return;
      }

      this.trackedSessionUserId = userId;
      queueMicrotask(() => {
        this.refreshUserDisplay();
        this.getUserProfiledata(userId);
        this.userSettingsService.clearSettings();
        this.preloadUserSettings();
      });
    });
  }

  ngOnInit(): void {
    this.userName = localStorage.getItem('userName');
    this.profileimage = localStorage.getItem('userImage') || '';
    this.userId = localStorage.getItem('userID');
    this.trackedSessionUserId = this.userId;
    this.userImage = (this.profileimage && this.profileimage.trim()) ? this.profileimage : null;
    
    // Check if we're on clients page and set isScrolled accordingly
    this.updateScrollStateForRoute();
    
    // Initialize client summary button visibility based on current route
    this.updateClientSummaryButtonVisibility();
    
    this.subscription.add(
      this.clientSummarySync.openSlider$.subscribe((request) => {
        this.sliderOpenRequest = request;
        this.showClientSummarySlider = true;
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.navigationService.getHeaderItems().subscribe(items => {
        this.headerItems = items;
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.navigationService.getActiveHeader().subscribe(header => {
        this.activeHeader = header;
        this.cdr.detectChanges();
      })
    );

    // Subscribe to query params changes for real-time updates when query params change without route change
    this.subscription.add(
      this.route.queryParams.subscribe(params => {
        // Only sync if we're on the search route
        const currentUrl = this.router.url;
        if (currentUrl.includes('/search')) {
          if (params['q']) {
            this.searchQuery.set(params['q']);
          } else {
            this.searchQuery.set('');
          }
          this.cdr.detectChanges();
        }
      })
    );

    // Cross-tab sync for recently viewed items
    this._storageListener = (event: StorageEvent) => {
      if (
        event.key === 'lastEditedProjectId' ||
        event.key === 'lastEditedProjectName'
      ) {
        this.loadRecentItems();
      } else if (event.key === 'userImage') {
        // Update profile image when changed in another tab
        this.updateUserImage(event.newValue || '');
      }
    };
    window.addEventListener('storage', this._storageListener);
    this.getUserProfiledata(this.userId || undefined);

    this.preloadUserSettings();

    // Initial sync of search query if already on search route (e.g., page refresh)
    if (this.router.url.includes('/search')) {
      const snapshot = this.route.snapshot;
      const queryParams = snapshot.queryParams;
      if (queryParams['q']) {
        this.searchQuery.set(queryParams['q']);
        this.cdr.detectChanges();
      }
    }
  }

  ngAfterViewInit() {
    // Delay the initial check to ensure DOM is fully rendered
    setTimeout(() => {
      this.checkScrollability();
      this.checkForArrows();
      this.setupScrollListener();
      // Ensure scroll state is correct for current route
      this.updateScrollStateForRoute();
      this.cdr.detectChanges();
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();

    // Clean up scroll listener
    if (this.scrollListener) {
      const container = this.itemsContainer?.nativeElement;
      if (container) {
        container.removeEventListener('scroll', this.scrollListener);
      }
    }

    // Clean up all click listeners
    this.clickListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.clickListeners = [];

    if (this.themeIconAnimTimer) {
      clearTimeout(this.themeIconAnimTimer);
      this.themeIconAnimTimer = null;
    }

    window.clearTimeout(this.headerMoreCloseTimer);

    // Remove storage event listener for cross-tab sync
    if (this._storageListener) {
      window.removeEventListener('storage', this._storageListener);
      this._storageListener = undefined;
    }
  }

  setupScrollListener() {
    const container = this.itemsContainer?.nativeElement;
    if (container) {
      this.scrollListener = () => {
        this.checkScrollability();
        this.cdr.detectChanges();
      };
      container.addEventListener('scroll', this.scrollListener);
    }
  }

  checkScrollability() {
    const container = this.itemsContainer?.nativeElement;
    if (container) {
      this.canScrollLeft = container.scrollLeft > 0;
      this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 1); // Added -1 for better detection
      this.cdr.detectChanges();
    }
  }

  scrollRecentItems(direction: 'left' | 'right') {
    const container = this.itemsContainer?.nativeElement;
    if (!container) return;

    const scrollAmount = 200; // Adjust this value based on your needs

    // Add sliding class
    container.classList.add('sliding');

    // Calculate target scroll position
    const currentScroll = container.scrollLeft;
    const targetScroll = direction === 'left' 
      ? currentScroll - scrollAmount 
      : currentScroll + scrollAmount;

    // Use scrollTo with smooth behavior for proper animation
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    // Check scrollability after animation
    setTimeout(() => {
      this.checkScrollability();
      this.checkForArrows();
      // Remove sliding class after animation
      container.classList.remove('sliding');
      this.cdr.detectChanges();
    }, 300);
  }
  isScrolled = false;
  isClientPageScrolled = false; // Add separate property for client pages
  
  @HostListener('window:scroll')
  onWindowScroll() {
    const currentUrl = this.router.url;
    
    // Use the same scrolled logic for all pages including client pages
    this.isScrolled = window.pageYOffset > -10;
    this.isClientPageScrolled = false; // Reset client scroll state for all pages
    
    this.cdr.detectChanges();
  }

  @HostListener('window:resize')
  onWindowResize() {
    // Recheck scrollability when window is resized
    setTimeout(() => {
      this.checkScrollability();
      this.checkForArrows();
      this.cdr.detectChanges();
    }, 100);
  }

  toggleNav() {
    this.navActive = !this.navActive;
    const navLinks = document.querySelector('.nav-links');
    const toggleButton = document.querySelector('.mobile-nav-toggle');

    if (navLinks) {
      if (this.navActive) {
        navLinks.classList.add('active');
        toggleButton?.classList.add('active');
        // Add click-outside listener
        setTimeout(() => {
          const handler = this.handleNavLinksOutsideClick as EventListener;
          document.addEventListener('click', handler, true);
          this.clickListeners.push({ element: document, event: 'click', handler });
        });
      } else {
        navLinks.classList.remove('active');
        toggleButton?.classList.remove('active');
        // Remove click-outside listener
        const handler = this.handleNavLinksOutsideClick as EventListener;
        document.removeEventListener('click', handler, true);
        this.clickListeners = this.clickListeners.filter(
          listener => !(listener.element === document && listener.handler === handler)
        );
      }
    }
    this.cdr.detectChanges();
  }

  // Handler to close nav-links when clicking outside
  handleNavLinksOutsideClick = (event: MouseEvent) => {
    const navLinks = document.querySelector('.nav-links');
    const toggleButton = document.querySelector('.mobile-nav-toggle');
    if (
      navLinks &&
      !navLinks.contains(event.target as Node) &&
      toggleButton &&
      !toggleButton.contains(event.target as Node)
    ) {
      navLinks.classList.remove('active');
      toggleButton.classList.remove('active');
      this.navActive = false;
      document.removeEventListener('click', this.handleNavLinksOutsideClick, true);
      this.cdr.detectChanges();
    }
  };

  onSearch() {
    // Navigate to search page with query parameters
    if (this.searchQuery().trim()) {
      this.router.navigate(['/search'], {
        queryParams: {
          q: this.searchQuery().trim(),
          category: this.selectedSearchOption()
        }
      });
    } else {
      // If no search query, just navigate with the selected category
      this.router.navigate(['/search'], {
        queryParams: {
          category: this.selectedSearchOption()
        }
      });
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
    this.triggerThemeIconAnimation();
    // Save theme mode to API
    this.themeManagementService.saveThemeMode().subscribe({
      next: (response) => {
        // if (response.success) {
        //   console.log('Theme mode saved successfully');
        // } else {
        //   console.error('Failed to save theme mode:', response.error || 'Unknown error');
        // }
      },
      error: (error) => {
        console.error('Error saving theme mode:', error);
      }
    });
    this.cdr.detectChanges();
  }

  private triggerThemeIconAnimation(): void {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    if (this.themeIconAnimTimer) {
      clearTimeout(this.themeIconAnimTimer);
    }

    this.themeIconAnimating = false;
    requestAnimationFrame(() => {
      this.themeIconAnimating = true;
      this.cdr.markForCheck();
      this.themeIconAnimTimer = setTimeout(() => {
        this.themeIconAnimating = false;
        this.themeIconAnimTimer = null;
        this.cdr.markForCheck();
      }, 780);
    });
  }

  getUserProfiledata(userId?: string): void {
    // If userId is not provided, use the one from localStorage 
    const id = userId || localStorage.getItem('userID');
    this.apiService.getuserProfileData(id || '').subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.userName = response.data.name || '';
          if (response.data.theme) {
            localStorage.setItem('selectedBackgroundTheme', response.data.theme);
          } else {
            localStorage.removeItem('selectedBackgroundTheme');
          }
          if (response.data.mode) {
            localStorage.setItem('userThemeMode', response.data.mode);
          }
          // Update profile image if available in API response
          if (response.data.profilePhoto || response.data.image) {
            const profileImage = response.data.image;
            this.updateUserImage(profileImage);
            localStorage.setItem('userImage', profileImage ?? '');
          } else {
            // Explicitly clear when no image is available
            this.updateUserImage('');
            localStorage.setItem('userImage', '');
          }

          // Force password change after admin/password reset — block app until updated
          if (response.data.isPasswordReset === true) {
            this.authService.setPasswordResetRequired(true);
            this.changePasswordClick.emit();
          } else {
            this.authService.setPasswordResetRequired(false);
          }
          if ('sidebarColor' in response.data || 'sidebarTheme' in response.data) {
            this.authService.patchUserData({
              sidebarColor:
                typeof response.data.sidebarColor === 'string'
                  ? response.data.sidebarColor
                  : '',
              sidebarTheme:
                typeof response.data.sidebarTheme === 'string'
                  ? response.data.sidebarTheme
                  : '',
            });
          }
        }
      },
      error: (error) => {
        console.error('Error fetching user profile data:', error);
      }
    }); 
   }

  toggleSearchDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.searchDropdownOpen.set(!this.searchDropdownOpen());
    this.cdr.detectChanges();

    if (this.searchDropdownOpen()) {
      const closeDropdown: EventListener = () => {
        this.searchDropdownOpen.set(false);
        this.cdr.detectChanges();
        document.removeEventListener('click', closeDropdown);
        this.clickListeners = this.clickListeners.filter(
          listener => !(listener.element === document && listener.handler === closeDropdown)
        );
      };
      document.addEventListener('click', closeDropdown);
      this.clickListeners.push({ element: document, event: 'click', handler: closeDropdown });
    }
  }

  selectSearchOption(option: string): void {
    this.selectedSearchOption.set(option);
    this.searchCategoryService.setSelectedCategory(option);
    this.searchDropdownOpen.set(false);
    this.cdr.detectChanges();
    
  }
getSearchOptionDisplayLabel(option: string): string {
    if (option === 'Portfolios') return 'Clients';
    if (option === 'REQUEST' || option === 'Requests') return 'Requests';
    return option;
  }
  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.dropdownOpen.set(!this.dropdownOpen());
    this.cdr.detectChanges();
    this.isDropdownOpen = !this.isDropdownOpen;

    // Emit dropdown state to layout component
    this.profileDropdownStateChange.emit({
      isOpen: this.dropdownOpen(),
      userName: this.userName,
      userImage: this.userImage
    });

    if (this.dropdownOpen()) {
      const closeDropdown: EventListener = () => {
        // Keep profile dropdown open while sign-out API is in progress
        if (this.logoutLoading()) {
          return;
        }
        this.dropdownOpen.set(false);
        this.cdr.detectChanges();
        this.isDropdownOpen = false;
        // Emit closed state
        this.profileDropdownStateChange.emit({
          isOpen: false,
          userName: this.userName,
          userImage: this.userImage
        });
        document.removeEventListener('click', closeDropdown);
        this.clickListeners = this.clickListeners.filter(
          listener => !(listener.element === document && listener.handler === closeDropdown)
        );
      };
      document.addEventListener('click', closeDropdown);
      this.clickListeners.push({ element: document, event: 'click', handler: closeDropdown });
    }
  }

  toggleNotifications(event: Event): void {
    event.stopPropagation();
    
    // Clean up any existing notification click listeners
    this.cleanupNotificationListeners();
    
    this.notificationsOpen.set(!this.notificationsOpen());
    this.notificationService.setPanelOpen(this.notificationsOpen());
   if (this.notificationsOpen()) {
      this.notificationService.smartRefreshNotifications();
    }

    this.cdr.detectChanges();

    if (this.notificationsOpen()) {
      // Add a small delay to ensure the dropdown is rendered in the DOM
      setTimeout(() => {
        const closeNotifications: EventListener = (e: Event) => {
          // Check if click is outside notifications panel
          const target = e.target as HTMLElement;
          const notificationsPanel = document.querySelector('.notification-dropdown');
          const notificationsToggle = document.querySelector('.notifications-toggle');
          
          // Don't close if clicking on the dropdown itself or the toggle button
          if (notificationsPanel && !notificationsPanel.contains(target) && 
              notificationsToggle && !notificationsToggle.contains(target)) {
            this.notificationsOpen.set(false);
            this.notificationService.setPanelOpen(false);
            this.cdr.detectChanges();
            this.cleanupNotificationListeners();
          }
        };
        document.addEventListener('click', closeNotifications);
        this.clickListeners.push({ element: document, event: 'click', handler: closeNotifications });
      }, 10);
    }
  }

  private cleanupNotificationListeners(): void {
    // Remove all click listeners for notifications
    this.clickListeners = this.clickListeners.filter(listener => {
      if (listener.element === document && listener.event === 'click') {
        document.removeEventListener('click', listener.handler);
        return false; // Remove from array
      }
      return true; // Keep in array
    });
  }

  closeNotificationDropdown(): void {
    this.notificationsOpen.set(false);
    this.notificationService.setPanelOpen(false);
    this.cleanupNotificationListeners();
    this.cdr.detectChanges();
  }

  onViewAllNotifications(): void {
    this.closeNotificationDropdown();
  }

  onNotificationClicked(): void {
    // Close the dropdown when a notification is clicked
    this.closeNotificationDropdown();
  }

  loadRecentItems() {
    this.apiService.getRecentlyViewed().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Frontend fallback: Move last edited project to top if present
          const lastEditedProjectId = localStorage.getItem('lastEditedProjectId');
          const lastEditedProjectName = localStorage.getItem('lastEditedProjectName');
          let items = response.data;
          if (lastEditedProjectId) {
            const idx = items.findIndex((item: any) => item._id === lastEditedProjectId);
            if (idx > -1) {
              if (lastEditedProjectName) {
                items[idx].projectName = lastEditedProjectName;
              }
              if (idx > 0) {
                const [edited] = items.splice(idx, 1);
                items.unshift(edited);
              }
            }
            localStorage.removeItem('lastEditedProjectId');
            localStorage.removeItem('lastEditedProjectName');
          }
          // Remove duplicate projects by _id, keeping the first occurrence
          const seen = new Set();
          items = items.filter((item: any) => {
            if (seen.has(item._id)) return false;
            seen.add(item._id);
            return true;
          });
          // Safety check: Filter out any items that might be null or undefined
          // This helps handle cases where deleted projects might still be in the response
          items = items.filter((item: any) => item && item._id && item.projectName);
          this.recentItems.set(items);
          // console.log('Recent items loaded:', this.recentItems());
          // Check scrollability after data is loaded
          setTimeout(() => {
            this.checkScrollability();
            this.checkForArrows();
            this.setupScrollListener();
            this.cdr.detectChanges();
          }, 100);
        } else {
          // If no data or unsuccessful response, set empty array
          this.recentItems.set([]);
        }
      },
      error: (error) => {
        console.error('Error fetching recent items:', error);
        // Set empty array on error to prevent showing stale data
        this.recentItems.set([]);
      }
    });
  }

  onRecentItemClick(item: { _id: string, projectName: string }, event: Event) {
    // Only prevent default for left-click to allow right-click context menu
    if (event.type === 'click') {
      event.preventDefault(); // Prevent default link behavior for left-click

      // Open the item's page in a new tab programmatically
      const url = `#/projects/project-details/${item._id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    // For right-click and other events, let the default link behavior work
  }


  logout(): void {
    if (this.logoutLoading()) {
      return;
    }

    this.logoutLoading.set(true);
    this.logoutLoadingChange.emit(true);

    // Preserve theme data before clearing localStorage
    const savedTheme = localStorage.getItem('selectedBackgroundTheme');
    const isThemeActive = localStorage.getItem('theme-background-active') === 'true';

    // Clear theme-background-active class to prevent blurred login page
    document.body.classList.remove('theme-background-active');
    document.body.style.backgroundImage = '';
    this.userSettingsService.clearSettings();
    localStorage.clear();
    sessionStorage.clear();

    // Restore theme data for next login if it was active
    if (savedTheme && isThemeActive) {
      localStorage.setItem('selectedBackgroundTheme', savedTheme);
      localStorage.setItem('theme-background-active', 'true');
    }

    this.authService.logout().subscribe({
      complete: () => {
        this.finishLogoutLoading();
      },
      error: () => {
        this.finishLogoutLoading();
      }
    });
  }

  private finishLogoutLoading(): void {
    if (!this.logoutLoading()) {
      return;
    }
    this.logoutLoading.set(false);
    this.logoutLoadingChange.emit(false);
    this.cdr.detectChanges();
  }


  checkForArrows() {
    // Check if there are enough items to scroll
    const container = this.itemsContainer?.nativeElement;
    if (container && this.recentItems().length > 0) {
      // Show arrows if content is wider than container
      const isScrollable = container.scrollWidth > container.clientWidth;
      this.showRecentArrows = isScrollable;
      
      // Also update scroll state
      if (isScrollable) {
        this.canScrollLeft = container.scrollLeft > 0;
        this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 1);
      } else {
        this.canScrollLeft = false;
        this.canScrollRight = false;
      }
      this.cdr.detectChanges();
    } else {
      this.showRecentArrows = false;
      this.canScrollLeft = false;
      this.canScrollRight = false;
      this.cdr.detectChanges();
    }
  }

  // onHeaderClick(header: NavigationItem): void {
  //   this.navigationService.setActiveHeader(header);
  //   this.router.navigate([header.to]);
  //   // Remove active class from nav-links on mobile
  //   if (window.innerWidth <= 1199) {
  //     const navLinks = document.querySelector('.nav-links');
  //     const toggleButton = document.querySelector('.mobile-nav-toggle');
  //     navLinks?.classList.remove('active');
  //     toggleButton?.classList.remove('active');
  //     this.navActive = false;
  //     document.removeEventListener('click', this.handleNavLinksOutsideClick, true);
  //   }
  //   this.cdr.detectChanges();
  // }
  onHeaderNavClick(event: MouseEvent, header: NavigationItem): void {
    if (
      this.timesheetStatusService.submissionRequired() &&
      !this.timesheetStatusService.isTimesheetNavItem(header)
    ) {
      event.preventDefault();
      this.timesheetStatusService.openSubmissionWarning();
      this.closeMobileNavMenu();
      return;
    }

    this.navigationService.setActiveHeader(header);
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    const url = header.to?.trim();
    if (!url) {
      return;
    }

    const targetUrl = url.startsWith('/') ? url : `/${url}`;
    if (this.router.url.split('?')[0] === targetUrl.split('?')[0]) {
      return;
    }

    void this.router.navigateByUrl(targetUrl);
    this.closeMobileNavMenu();
  }

  private closeMobileNavMenu(): void {
    if (window.innerWidth <= 1199) {
      const navLinks = document.querySelector('.nav-links');
      const toggleButton = document.querySelector('.mobile-nav-toggle');
      navLinks?.classList.remove('active');
      toggleButton?.classList.remove('active');
      this.navActive = false;
      document.removeEventListener('click', this.handleNavLinksOutsideClick, true);
    }
    this.cdr.detectChanges();
  }

  isHeaderActive(header: NavigationItem): boolean {
    // Use the navigation service's logic exclusively to prevent conflicts
    return this.navigationService.isHeaderActive(header);
  }

  /** When timesheet submission is required, only Timesheet stays highlighted. */
  isHeaderNavHighlighted(header: NavigationItem): boolean {
    if (this.timesheetStatusService.headerAttention()) {
      return this.timesheetStatusService.isTimesheetNavItem(header);
    }
    return this.isHeaderActive(header);
  }

  isReportsNavItem(header: NavigationItem): boolean {
    const label = (header.label || header.name || '').trim().toLowerCase();
    return label === 'reports' || header.to === '/reports';
  }

  isHeaderMoreButton(item: NavigationItem): boolean {
    const isMoreButton = item.type === 'button' || Array.isArray(item.dropDown);
    return isMoreButton && this.getHeaderMoreItems(item).length > 0;
  }

  getHeaderMoreItems(item: NavigationItem): NavigationItem[] {
    return (item.dropDown ?? []).filter((dropItem) => !!(dropItem.label || '').trim());
  }

  getHeaderMoreItemRoute(item: NavigationItem): string {
    const own = (item.to || '').trim();
    if (own && own !== '/') {
      return own.startsWith('/') ? own : `/${own}`;
    }
    const firstSub = (item.subs ?? []).find((sub) => (sub.to || '').trim());
    return (firstSub?.to || '').trim();
  }

  isHeaderMoreRouteActive(to: string | undefined): boolean {
    const url = (to || '').trim();
    if (!url || url === '/') {
      return false;
    }
    const current = this.router.url.split('?')[0];
    const target = url.startsWith('/') ? url : `/${url}`;
    return current === target || current.startsWith(`${target}/`);
  }

  isHeaderMoreItemActive(item: NavigationItem): boolean {
    return (
      this.isHeaderMoreRouteActive(this.getHeaderMoreItemRoute(item)) ||
      (item.subs ?? []).some((sub) => this.isHeaderMoreRouteActive(sub.to))
    );
  }

  isHeaderMoreHighlighted(item: NavigationItem): boolean {
    return this.getHeaderMoreItems(item).some((dropItem) => this.isHeaderMoreItemActive(dropItem));
  }

  onHeaderMoreItemClick(event: MouseEvent, to: string | undefined, section?: NavigationItem): void {
    event.stopPropagation();
    this.closeHeaderMoreAfterSelect();

    const url = (to || '').trim();
    if (!url) {
      event.preventDefault();
      return;
    }

    if (this.timesheetStatusService.submissionRequired()) {
      event.preventDefault();
      this.timesheetStatusService.openSubmissionWarning();
      this.closeMobileNavMenu();
      return;
    }

    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    const targetUrl = url.startsWith('/') ? url : `/${url}`;
    this.activateHeaderMoreSection(section, targetUrl);
    if (this.router.url.split('?')[0] === targetUrl.split('?')[0]) {
      this.closeMobileNavMenu();
      return;
    }

    void this.router.navigateByUrl(targetUrl);
    this.closeMobileNavMenu();
  }

  private activateHeaderMoreSection(section: NavigationItem | undefined, targetUrl: string): void {
    if (!section) {
      return;
    }
    this.navigationService.setActiveHeader(section);
    const matchedSub =
      (section.subs ?? []).find((sub: NavigationSubItem) => sub.to === targetUrl) ||
      (section.subs ?? [])[0] ||
      null;
    if (matchedSub) {
      this.navigationService.setActiveSubItem(matchedSub);
    }
  }

  openHeaderMore(): void {
    if (this.headerMoreLockedClosed) {
      return;
    }
    window.clearTimeout(this.headerMoreCloseTimer);
    this.headerMoreOpen.set(true);
  }

  scheduleCloseHeaderMore(): void {
    this.headerMoreLockedClosed = false;
    window.clearTimeout(this.headerMoreCloseTimer);
    this.headerMoreCloseTimer = window.setTimeout(() => this.headerMoreOpen.set(false), 220);
  }

  toggleHeaderMore(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    window.clearTimeout(this.headerMoreCloseTimer);
    this.headerMoreLockedClosed = false;
    this.headerMoreOpen.update((open) => !open);
  }

  private closeHeaderMoreAfterSelect(): void {
    window.clearTimeout(this.headerMoreCloseTimer);
    this.headerMoreLockedClosed = true;
    this.headerMoreOpen.set(false);
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
  }

  @HostListener('document:click')
  closeHeaderMoreFromOutside(): void {
    this.headerMoreLockedClosed = false;
    if (this.headerMoreOpen()) {
      this.headerMoreOpen.set(false);
    }
  }

  isTimesheetAttentionItem(header: NavigationItem): boolean {
    return (
      this.timesheetStatusService.headerAttention() &&
      this.timesheetStatusService.isTimesheetNavItem(header)
    );
  }

  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.layoutService.toggleSidebar();
  }

  onThemeBtnClick() {
    this.themeBtnClick.emit();
  }



  toggleChangePasswordForm() {
    this.changePasswordClick.emit();
  }

  toggleRecentlyViewed() {
    this.recentlyViewedExpanded.set(!this.recentlyViewedExpanded());
    
    // Check scrollability and arrows after toggle to ensure arrows are visible
    // Use a longer timeout to allow CSS transitions to complete
    setTimeout(() => {
      this.checkScrollability();
      this.checkForArrows();
      this.cdr.detectChanges();
    }, 350); // Increased to match CSS transition duration (0.3s)
  }

  onClientSummaryClick(): void {
    this.sliderOpenRequest = null;
    this.showClientSummarySlider = true;
  }

  onCloseClientSummarySlider(): void {
    this.showClientSummarySlider = false;
    this.sliderOpenRequest = null;
  }

  getSliderContext(): { objCode: string; objID: string } {
    if (this.sliderOpenRequest?.portfolioId) {
      return {
        objCode: 'PORTFOLIO',
        objID: this.sliderOpenRequest.portfolioId,
      };
    }
    return this.getCurrentContext();
  }

  getSliderContentFilter(): ClientSummarySliderFilter {
    return this.sliderOpenRequest?.filter ?? 'all';
  }

  getSliderCompanyId(): string | number | null {
    return this.sliderOpenRequest?.companyId ?? null;
  }

  getSliderInitialInvoices(): any[] | null {
    return this.sliderOpenRequest?.invoices ?? null;
  }

  getSliderInvoiceFromAddress(): any | null {
    return this.sliderOpenRequest?.invoiceFromAddress ?? null;
  }

  onMyWidgetsClick(): void {
    this.showMyWidgetsSlider = true;
  }

  onCloseMyWidgetsSlider(): void {
    this.showMyWidgetsSlider = false;
    this.myWidgetsSliderClosing = true;
    // Keep header z-index elevated until leave animation completes (220ms)
    setTimeout(() => {
      this.myWidgetsSliderClosing = false;
    }, 250);
  }

  onFeedbackClick(): void {
    this.notificationsOpen.set(false);
    this.showFeedbackSlider = true;
  }

  onCloseFeedbackSlider(): void {
    this.showFeedbackSlider = false;
  }

  getCurrentContext(): { objCode: string, objID: string } {
    const currentUrl = this.router.url;
    const urlParts = currentUrl.split('/').filter(Boolean); // remove empty parts
  
    // 🔹 PROJECT context
    const projectIndex = urlParts.findIndex(
      part => part === 'projects' || part === 'project-details'
    );
  
    if (projectIndex !== -1) {
      // Handle cases like:
      // /projects/project-details/:id
      // /project-details/:id
      const possibleId = urlParts[projectIndex + 1] === 'project-details'
        ? urlParts[projectIndex + 2]
        : urlParts[projectIndex + 1];
  
      if (possibleId) {
        return {
          objCode: 'PROJECT',
          objID: possibleId
        };
      }
    }
  
    // 🔹 CLIENT context
    const clientIndex = urlParts.findIndex(
      part => part === 'clients' || part === 'client-details'
    );
  
    if (clientIndex !== -1) {
      const possibleId = urlParts[clientIndex + 1] === 'client-details'
        ? urlParts[clientIndex + 2]
        : urlParts[clientIndex + 1];
  
      if (possibleId) {
        return {
          objCode: 'PORTFOLIO',
          objID: possibleId
        };
      }
    }
  
    // 🔹 Fallback to stored context
    const storedClientId = localStorage.getItem('currentClientId');
    const storedProjectId = localStorage.getItem('currentProjectId');
  
    if (storedProjectId) {
      return { objCode: 'PROJECT', objID: storedProjectId };
    }
  
    if (storedClientId) {
      return { objCode: 'PORTFOLIO', objID: storedClientId };
    }
  
    // 🔹 Final fallback
    return { objCode: 'PORTFOLIO', objID: 'default-client-id' };
  }
  
  onCollapsedSearchSectionClick(event: Event): void {
    if (!this.recentlyViewedExpanded() || this.searchDisabled()) {
      return;
    }
    event.preventDefault();
    this.expandSearchAndFocus();
  }

  onSearchButtonClick(event: Event): void {
    if (this.recentlyViewedExpanded()) {
      event.preventDefault();
      event.stopPropagation();
      this.expandSearchAndFocus();
      return;
    }
    this.onSearch();
  }

  expandSearchSection(): void {
    this.expandSearchAndFocus();
  }

  expandSearchAndFocus(): void {
    if (this.searchDisabled()) {
      return;
    }

    this.recentlyViewedExpanded.set(false);
    this.cdr.detectChanges();

    setTimeout(() => {
      this.headerSearchInput?.nativeElement?.focus();
    });

    setTimeout(() => {
      this.checkScrollability();
      this.checkForArrows();
      this.cdr.detectChanges();
    }, 350);
  }

  onSupportClick(): void {
    this.router.navigate(['/support']);
  }

  onSettingsClick(): void {
    // Emit event to parent layout component
    this.settingsClick.emit();
  }

  onSettingsSaved(settings: UserSettings): void {
    // Update local user data if needed
    if (settings.name) {
      this.userName = settings.name;
      localStorage.setItem('userName', settings.name);
    }
    // Update profile image if changed
    if (settings.profilePhoto !== undefined) {
      this.updateUserImage(settings.profilePhoto);
    }
  }

  private preloadUserSettings(): void {
    // Only preload if settings are not already loaded
    if (!this.userSettingsService.areSettingsLoaded() && !this.userSettingsService.areSettingsLoading()) {
      this.userSettingsService.loadUserSettings().subscribe({
        next: (response) => {
          this.userSettingsService.processUserSettings(response);
        },
        error: (error) => {
          console.error('Error preloading user settings:', error);
        }
      });
    }
  }

  onPeopleClick(event: MouseEvent): void {
    // Allow browser default for new-tab / modified clicks (right-click menu, ctrl/cmd-click, middle-click).
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    void this.router.navigate(['/people']);
  }

  toggleSearchDisabled(): void {
    this.searchDisabled.set(!this.searchDisabled());
    this.cdr.detectChanges();
  }

  navigateToUserProfile(event: MouseEvent): void {
    event.stopPropagation();
    if (this.userId) {
      this.dropdownOpen.set(false);
      this.isDropdownOpen = false;
      this.router.navigate(['/people/peopleview', this.userId]);
    }
  }


  enableSearch(): void {
    this.searchDisabled.set(false);
    this.cdr.detectChanges();
  }

  disableSearch(): void {
    this.searchDisabled.set(true);
    this.cdr.detectChanges();
  }



  toggleMoreDropdown(event: MouseEvent): void {
    event.stopPropagation();
    
    // Toggle the dropdown for all devices
    this.moreDropdownOpen.set(!this.moreDropdownOpen());
    this.cdr.detectChanges();

    if (this.moreDropdownOpen()) {
      const closeDropdown: EventListener = () => {
        this.moreDropdownOpen.set(false);
        this.cdr.detectChanges();
        document.removeEventListener('click', closeDropdown);
        this.clickListeners = this.clickListeners.filter(
          listener => !(listener.element === document && listener.handler === closeDropdown)
        );
      };
      document.addEventListener('click', closeDropdown);
      this.clickListeners.push({ element: document, event: 'click', handler: closeDropdown });
    }
  }

  onMoreItemClick(item: string): void {
    this.moreDropdownOpen.set(false);
    this.cdr.detectChanges();
    
    // Handle navigation based on item
    switch (item) {
      case 'Queues':
        this.router.navigate(['/queues']);
        break;
      case 'Reports':
        this.router.navigate(['/reports']);
        break;
      case 'Analytics':
        this.router.navigate(['/analytics']);
        break;
      case 'Settings':
        this.router.navigate(['/settings']);
        break;
      case 'Help':
        this.router.navigate(['/help']);
        break;
      default:
        console.log('Unknown more item:', item);
    }
  }

  // Method to update user image
  private updateUserImage(imageUrl: string): void {
    this.profileimage = imageUrl;
    this.userImage = (imageUrl && imageUrl.trim()) ? imageUrl : null;
    this.cdr.detectChanges();
  }

  // Method to check if user has an image
  hasUserImage(): boolean {
    return !!(this.userImage && this.userImage.trim() && this.userImage !== 'assets/images/avatar.png');
  }

  // Method to handle avatar image error and fallback to placeholder
  onAvatarImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    // Show placeholder by setting userImage and profileimage to null
    this.userImage = null;
    this.profileimage = null;
    this.cdr.detectChanges();
  }

  getInitials(name: string): string {
    if (!name || name.trim() === '') {
      return '?';
    }

    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  getAvatarColor(userId: string): string {
    // Generate a consistent color based on user ID - Rich vibrant colors matching people page
    const colors = [
      '#0644C9', // Rich Vibrant Blue
      '#8B2CF5', // Rich Purple
      '#E91E63', // Rich Pink
      '#4CAF50', // Rich Green
      '#FF9800', // Rich Orange
      '#9C27B0', // Rich Magenta
      '#3F51B5', // Rich Indigo
      '#00BCD4', // Rich Cyan
      '#795548', // Rich Brown
      '#2196F3', // Rich Blue
      '#009688', // Rich Teal
      '#673AB7', // Rich Deep Purple
      '#FF6F00', // Rich Deep Orange
      '#1976D2', // Rich Dark Blue
      '#F44336', // Rich Red
      '#FFC107', // Rich Amber
      '#8BC34A', // Rich Light Green
      '#00E676', // Rich Light Green
      '#18FFFF', // Rich Light Cyan
      '#E1BEE7', // Rich Light Purple
      '#FFCDD2', // Rich Light Pink
      '#B39DDB', // Rich Light Purple
      '#9FA8DA', // Rich Light Indigo
      '#90CAF9', // Rich Light Blue
      '#81D4FA', // Rich Light Blue
      '#80DEEA', // Rich Light Cyan
      '#80CBC4', // Rich Light Teal
      '#A5D6A7', // Rich Light Green
      '#C8E6C9', // Rich Light Green
      '#DCEDC8', // Rich Light Green
      '#F0F4C3', // Rich Light Lime
      '#FFF9C4', // Rich Light Yellow
      '#FFECB3', // Rich Light Orange
      '#FFE0B2', // Rich Light Orange
      '#FFCCBC', // Rich Light Orange
      '#FFAB91', // Rich Light Orange
      '#BCAAA4', // Rich Light Brown
      '#D7CCC8', // Rich Light Brown
      '#F5F5F5', // Rich Light Grey
      '#EEEEEE', // Rich Light Grey
      '#E0E0E0', // Rich Light Grey
    ];
    if (!userId || userId.trim() === '') {
      return colors[0]; // Default color if no userId
    }
    const index = userId.charCodeAt(0) % colors.length;
    return colors[index];
  }

  // Public method to refresh user image from localStorage
  public refreshUserImage(): void {
    const storedImage = localStorage.getItem('userImage');
    this.updateUserImage(storedImage || '');
  }

  public refreshUserDisplay(): void {
    this.userName = localStorage.getItem('userName');
    this.userId = localStorage.getItem('userID');
    this.refreshUserImage();
    this.cdr.detectChanges();
  }

  onSwitchBackToOriginalAccount(): void {
    if (this.switchBackLoading()) {
      return;
    }

    const originalName = this.authService.getImpersonationOriginalUserName();
    this.switchBackLoading.set(true);
    this.switchBackLoadingChange.emit(true);

    this.authService.switchBackToOriginalAccount().subscribe({
      next: (response: any) => {
        const userData = response?.data?.userData || response?.userData;
        this.navigationService.reloadNavigation();
        this.refreshUserDisplay();
        this.accountSwitchedBack.emit();

        const redirectPath = userData?.redirectTo || '/dashboard';
        this.router.navigateByUrl(redirectPath, { replaceUrl: true });
        this.toastr.success(`Switched back to ${originalName || 'your account'}`);
        this.switchBackLoading.set(false);
        this.switchBackLoadingChange.emit(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.switchBackLoading.set(false);
        this.switchBackLoadingChange.emit(false);
        this.toastr.error('Unable to switch back to your account.');
        this.cdr.detectChanges();
      }
    });
  }

  // Method to force update scroll state based on current route
  private updateScrollStateForRoute(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/clients') || currentUrl.includes('/clients-list')) {
      this.isScrolled = false;
      this.isClientPageScrolled = false; // Reset client scroll state
      this.cdr.detectChanges();
      
      // Remove scrolled class from DOM
      setTimeout(() => {
        const headerElement = document.querySelector('.header-menu');
        if (headerElement) {
          headerElement.classList.remove('scrolled');
        }
      }, 0);
    } else {
      // Reset client scroll state for non-client pages
      this.isClientPageScrolled = false;
    }
  }

  // Method to update client summary button visibility
  private updateClientSummaryButtonVisibility(): void {
    const currentUrl = this.router.url;
    this.showClientSummaryButton = this.allowedRoutes.some(route =>
      currentUrl.includes(route)
    );
    this.cdr.detectChanges();
  }

  // Footer menu methods
  onFooterMenuClick(menuItem: string): void {
    switch (menuItem) {
      case 'dashboard':
        this.closeAddRequestForm();
        this.router.navigate(['/dashboard']);
        break;
      case 'requests':
        // ≤1199: open add-request in place (no listing route). If last week is not
        // submitted, show ts-submit-popup instead — same gate as footer Dashboard.
        if (typeof window !== 'undefined' && window.innerWidth <= 1199) {
          this.timesheetStatusService.ensureStatus().pipe(take(1)).subscribe((status) => {
            if (!status.success) {
              this.closeAddRequestForm();
              this.timesheetStatusService.openSubmissionWarning();
              this.cdr.detectChanges();
              return;
            }
            this.showAddRequestForm = true;
            this.cdr.detectChanges();
          });
          return;
        }
        this.router.navigate(['/requests']);
        break;
      case 'timesheets':
        this.closeAddRequestForm();
        this.router.navigate(['/timesheets'], {
          queryParams: { tab: 'timesheet', forceCurrentWeek: '1' },
        });
        break;
      case 'history':
        this.closeAddRequestForm();
        this.router.navigate(['/history']);
        break;
      default:
        console.log('Unknown footer menu item:', menuItem);
    }
  }

  isFooterMenuActive(menuItem: string): boolean {
    const currentUrl = this.router.url;
    
    switch (menuItem) {
      case 'dashboard':
        // Only match exact dashboard routes
        return !this.showAddRequestForm && (currentUrl === '/' || currentUrl === '/dashboard');
      case 'requests':
        if (this.showAddRequestForm) {
          return true;
        }
        return currentUrl === '/requests' || currentUrl.startsWith('/requests?');
      case 'timesheets':
        // Match timesheets page and any sub-routes
        return !this.showAddRequestForm && currentUrl.startsWith('/timesheets');
      case 'history':
        // Match history page and any sub-routes
        return !this.showAddRequestForm && (currentUrl === '/history' || currentUrl.startsWith('/history/'));
      default:
        return false;
    }
  }

  closeAddRequestForm(): void {
    this.showAddRequestForm = false;
  }

  onHeaderAddRequestSubmitted(request: unknown): void {
    this.requestService.createRequest(request).subscribe({
      next: (res: any) => {
        this.toastr.success(res?.message || 'Request submitted');
        this.showAddRequestForm = false;
        const requestId = res?.data?._id;
        if (requestId) {
          this.crossTabSyncService.sendGenericSync('request_created', { requestId });
        }
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.toastr.error(err?.message || 'Failed to submit request');
        this.headerAddRequestForm?.resetSubmitting();
      },
    });
  }

}

